/**
 * Shared types for the AI usage dashboard components.
 *
 * `UsageDisplayRow` is a camelCase view of the backend's `SummaryRow`
 * (see `@services/aiUsageService`). There is deliberately no
 * `priceSource` / provenance field here: the backend does not return one
 * (see `backend/features/ai_gateway/schemas.py::SummaryRow`), so nothing
 * in this feature invents, hardcodes, or derives one.
 */

export type UsageDimension = 'user' | 'project' | 'model' | 'provider' | 'client';

export const USAGE_DIMENSIONS: UsageDimension[] = ['user', 'project', 'model', 'provider', 'client'];

export interface UsageDisplayRow {
  /** The stable grouping key -- an opaque user id. Displayed only when
   * `userLabel` is null (a deleted user, or one of the non-UUID probe ids
   * the rollup can contain). */
  user: string | null;
  /** The user's email, resolved server-side. Display only. */
  userLabel: string | null;
  /** The stable grouping key -- an opaque project id. See `user`. */
  project: string | null;
  /** The project's path ("agentic/outpost"), resolved server-side. */
  projectLabel: string | null;
  model: string | null;
  provider: string | null;
  /** Which tool spent the tokens (#114): "claude-code", "unknown", ... */
  client: string | null;
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  /**
   * Cache WRITE tokens -- the expensive cache tier (a cache write costs
   * more than an uncached input token; a cache read costs a fraction of
   * one). Shown alongside cacheReadTokens so the table doesn't hide the
   * costly half of cache usage.
   */
  cacheWriteTokens: number;
  requests: number;
  /**
   * `null` means "no cost applies" (local-model usage), never "$0.00".
   * Always render through `formatCost` / `sumNullableCost` -- never `?? 0`.
   */
  costUsd: number | null;
}
